import { ApiService } from "@/services/api-service";
import { MarkdownToJSX } from "@/components/markdowntojsx";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CalendarDays,
  ChevronLeft,
  Globe,
  Lock,
  Menu,
  Share2,
  X,
} from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { useEffect, useState, useRef } from "react";
import { Badge } from "@/components/ui/badge";

import { cn } from "@/lib/utils";
import { SharedProjectOutline } from "@/components/shared-project-outline";
import { createFileRoute, Link, useLoaderData } from "@tanstack/react-router";
import { seo } from "@/lib/seo";
import { useHandleClickOutside } from "@/hooks/useClickOutside";

export const Route = createFileRoute("/share/$shareId")({
  component: ShareProject,
  ssr: true,
  loader: async ({ params }) => {
    try {
      const projectData = await ApiService.getSharedProject(params.shareId);
      return projectData;
    } catch (error) {
      console.error("SSR Loader Error:", error);
      return null;
    }
  },
  head: ({ loaderData: data }) => {
    const title = `${data?.project?.name || "Shared Document"}`;
    const description = (() => {
      const rawText = data?.content?.replace(/#+/g, "") || "";
      const words = rawText.split(/\s+/).slice(0, 20);
      return words.join(" ") || "A shared document";
    })();

    // Improved regex to capture only the URL
    const imageRegex = /!\[.*?\]\((.*?)(?:\s+"[^"]*")?\)/;
    const firstImageMatch = data?.content?.match(imageRegex);
    const image =
      firstImageMatch?.[1] ||
      "https://res.cloudinary.com/dtcejk6h3/image/upload/v1747040895/og-image_ztxfrr.png";

    return {
      title,
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        ...seo({ title, description, image }),
      ],
    };
  },
});

function ShareProject() {
  const loaderData = useLoaderData({ from: "/share/$shareId" });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isBrowser, setIsBrowser] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsBrowser(true);
  }, []);

  const { shareId } = Route.useParams();
  const [data, setProjectData] = useState(loaderData);
  const [loading, setLoading] = useState(!loaderData);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!loaderData) {
      ApiService.getSharedProject(shareId)
        .then((data) => {
          setProjectData(data);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Client-side fetch failed:", err);
          setError("You are not authorized to view this private project.");
          setLoading(false);
        });
    }
  }, [loaderData, shareId]);

  useEffect(() => {
    if (!isBrowser) return;

    const handleResize = () => {
      setSidebarOpen(window.innerWidth >= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isBrowser]);

  useEffect(() => {
    if (!isBrowser || !contentRef.current || !data?.content) return;

    const addIdsToHeadings = () => {
      const headings = contentRef.current?.querySelectorAll(
        "h1, h2, h3, h4, h5, h6"
      );
      headings?.forEach((heading) => {
        if (!heading.id) {
          const text = heading.textContent || "";
          heading.id = text
            .toLowerCase()
            .replace(/[^\w\s-]/g, "")
            .replace(/\s+/g, "-");
        }
      });
    };

    setTimeout(addIdsToHeadings, 300);
  }, [data?.content, isBrowser]);

  // Handle outside click for mobile sidebar
  useHandleClickOutside(sidebarRef, () => {
    setSidebarOpen(false);
  });

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 text-center bg-gray-50 dark:bg-neutral-900">
        <div className="w-full max-w-3xl bg-white dark:bg-neutral-950 rounded-xl shadow-md p-8">
          <div className="mb-6">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Share2 className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-2">
              Unable to load shared document
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              This document may have been removed or you don't have permission
              to view it.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => isBrowser && window.history.back()}
            className="mx-auto"
          >
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const documentTitle = data?.project?.name || "Shared Document";

  const lastUpdated = data?.updated_at ? new Date(data.updated_at) : null;

  return (
    <>
      <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-neutral-900">
        <header className="sticky top-0 z-30 bg-white dark:bg-neutral-950 border-gray-200 dark:border-neutral-800 shadow-sm">
          <div className="container max-w-7xl mx-auto px-4 py-3 flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="mr-2"
              aria-label={sidebarOpen ? "Close outline" : "Open outline"}
            >
              {sidebarOpen && window.innerWidth >= 768 ? (
                <ChevronLeft className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </Button>
            <div className="text-md sm:text-lg font-semibold line-clamp-1 text-center flex-1">
              <span className="font-serif">{documentTitle}</span>
            </div>
            <div className="w-9"></div>
          </div>
        </header>

        <main className="flex-grow container max-w-7xl mx-auto flex">
          {sidebarOpen && data && (
            <div className="hidden md:block sticky top-[57px] self-start h-[calc(100vh-57px)] z-20">
              <SharedProjectOutline content={data.content} className="h-full" />
            </div>
          )}

          {/* Mobile sidebar - slides in from left with backdrop */}
          {sidebarOpen && data && isBrowser && window.innerWidth < 768 && (
            <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity">
              <div
                ref={sidebarRef}
                className="absolute left-0 top-0 bottom-0 w-[85%] max-w-[320px] bg-white dark:bg-neutral-950 h-screen shadow-xl transform transition-transform duration-300 ease-in-out"
              >
                <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-neutral-800">
                  <span className="font-medium text-emerald-600 dark:text-emerald-400 font-serif">
                    Table of Contents
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setSidebarOpen(false)}
                    className="h-8 w-8"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <SharedProjectOutline
                  content={data.content}
                  className="h-[calc(100%-56px)]"
                />
              </div>
            </div>
          )}

          <div
            className={cn("flex-grow px-4 py-8", sidebarOpen ? "md:pl-0" : "")}
          >
            {loading ? (
              <div className="bg-white dark:bg-neutral-950 rounded-xl shadow-md p-6 space-y-6">
                <Skeleton className="h-10 w-3/4" />
                <div className="flex gap-4">
                  <Skeleton className="h-5 w-24" />
                  <Skeleton className="h-5 w-32" />
                </div>
                <div className="space-y-4 mt-8 pt-6 border-t border-gray-100 dark:border-neutral-800">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton key={i} className="h-4 w-full" />
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-white dark:bg-neutral-950 max-w-3xl mx-auto rounded-xl shadow-md overflow-hidden">
                <div className="p-6 md:p-8 border-b border-gray-200 dark:border-neutral-800 flex justify-between">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600 dark:text-gray-400 border-gray-100 dark:border-neutral-800">
                    {lastUpdated && (
                      <div className="flex items-center gap-1.5">
                        <CalendarDays className="h-4 w-4" />
                        <span title={format(lastUpdated, "PPpp")}>
                          {format(lastUpdated, "MMMM d, yyyy")}
                        </span>
                      </div>
                    )}
                    <Badge
                      variant={
                        data.visibility === "PUBLIC" ? "default" : "outline"
                      }
                      className="capitalize"
                    >
                      {data.visibility === "PUBLIC" ? (
                        <Globe className="h-3 w-3 mr-1" />
                      ) : (
                        <Lock className="h-3 w-3 mr-1" />
                      )}
                      {data.visibility}
                    </Badge>
                  </div>
                  <div>
                    <Link
                      to={`/auth/signup`}
                      className="inline-flex items-center justify-center whitespace-nowrap px-4 py-2 text-[14px] font-normal text-white bg-secondary-700 rounded-[12px] capitalize"
                      aria-label="Start writing with Inkriv"
                    >
                      Try Inkriv
                    </Link>
                  </div>
                </div>

                {/* Article Content */}
                <div className="p-6 md:p-8">
                  <div
                    ref={contentRef}
                    className={cn(
                      "prose dark:prose-invert max-w-none",
                      "prose-lg", // Larger text for better readability
                      "prose-headings:font-serif prose-headings:font-bold prose-headings:scroll-mt-20",
                      "prose-p:text-gray-800 dark:prose-p:text-gray-200 prose-p:leading-relaxed",
                      "prose-a:text-emerald-600 dark:prose-a:text-emerald-400 prose-a:no-underline hover:prose-a:underline",
                      "prose-blockquote:border-l-emerald-500 prose-blockquote:bg-gray-50 dark:prose-blockquote:bg-neutral-900 prose-blockquote:py-1 prose-blockquote:px-4 prose-blockquote:italic",
                      "prose-code:bg-gray-100 dark:prose-code:bg-neutral-800 prose-code:rounded prose-code:px-1 prose-code:py-0.5 prose-code:text-sm",
                      "prose-pre:bg-gray-100 dark:prose-pre:bg-neutral-800 prose-pre:rounded-lg",
                      "prose-img:rounded-lg prose-img:mx-auto"
                    )}
                  >
                    {data.content ? (
                      <MarkdownToJSX text={data.content} />
                    ) : (
                      <div className="py-8 text-center">
                        <div className="w-16 h-16 bg-gray-100 dark:bg-neutral-800 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Share2 className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                        </div>
                        <p className="text-gray-500 dark:text-gray-400 italic">
                          This document is empty
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
}
